import { batch } from 'react-redux';
import { format } from 'date-fns';
import download from 'downloadjs';

import { showSuccessToast, showErrorToast } from '../../../shared/utils/toastManager';
import { axiosAuthed } from '../../../shared/utils/setCommonHeaders';
import { authProvider } from '../../login/ducks/loginActions';

import {
  CLOSE_BOARD,
  FETCH_EXERCISES_START,
  FETCH_EXERCISES_FULFILLED,
  FETCH_EXERCISES_REJECTED,
  FETCH_GROUPS_START,
  FETCH_GROUPS_FULFILLED,
  FETCH_GROUPS_REJECTED,
  FETCH_LEADERBOARD_START,
  FETCH_LEADERBOARD_FULFILLED,
  FETCH_LEADERBOARD_REJECTED,
  EDIT_LEADERBOARD_START,
  EDIT_LEADERBOARD_FULFILLED,
  STASH_PENDING_RELOAD,
  APPLY_PENDING_RELOAD,
  FETCH_LEADERBOARD_CSV_START,
  FETCH_LEADERBOARD_CSV_FULFILLED,
  FETCH_NEXT_PAGE_START,
  FETCH_NEXT_PAGE_FULFILLED,
  FETCH_NEXT_PAGE_REJECTED,
  UPDATE_SETTING,
  SET_MODAL_OPEN,
  FETCH_TEMPLATES_START,
  FETCH_TEMPLATES_FULFILLED,
  FETCH_TEMPLATES_REJECTED,
  SAVE_TEMPLATE_START,
  SAVE_TEMPLATE_FULFILLED,
  SAVE_TEMPLATE_REJECTED,
  UPDATE_TEMPLATE_START,
  UPDATE_TEMPLATE_FULFILLED,
  UPDATE_TEMPLATE_REJECTED,
  DELETE_TEMPLATE_START,
  DELETE_TEMPLATE_FULFILLED,
  DELETE_TEMPLATE_REJECTED,
  MARK_BOARD_AS_TEMPLATE,
  SET_LEADERBOARD_VIEW,
  SET_ACTIVE_FILTERS,
  CLEAR_ALL_BOARDS,
} from './actionTypes';

authProvider();

export const fetchExercises = (accountCode, exerciseType) => (
  (dispatch) => {
    dispatch({ type: FETCH_EXERCISES_START });
    axiosAuthed.get(`/accounts/${accountCode}/exercises?type=${exerciseType}&tracked=1`)
      .then((response) => {
        dispatch({
          type: FETCH_EXERCISES_FULFILLED,
          payload: response.data,
        });
      })
      .catch((err) => {
        dispatch({
          type: FETCH_EXERCISES_REJECTED,
          payload: err,
        });
      });
  }
);

export const fetchGroups = (accountCode) => (
  (dispatch) => {
    dispatch({ type: FETCH_GROUPS_START });
    axiosAuthed.get(`/accounts/${accountCode}/groups?leaderboard=1`)
      .then((response) => {
        dispatch({
          type: FETCH_GROUPS_FULFILLED,
          payload: response.data,
        });
      })
      .catch((err) => {
        dispatch({
          type: FETCH_GROUPS_REJECTED,
          payload: err,
        });
        if (err?.response?.status !== 403) {
          showErrorToast(err?.response?.headers?.message || 'Failed to load groups. Please try again.');
        }
      });
  }
);

// Assembles the leaderboard query string from Formik values. `page` must be passed
// explicitly by the caller (its meaning differs between a fresh fetch and loadMore).
export const buildLeaderboardQuery = (values, { page, isCsv = false } = {}) => {
  const {
    accountCode,
    compareTo,
    compareDates,
    exerciseId,
    gender,
    groups,
    mustBeInAllGroups,
    rangeType,
    rangeDates,
  } = values;

  const accountString = `/accounts/${accountCode}`;
  const exerciseQuery = `/exercises/${exerciseId}/`;
  const leaderboardQuery = `leaderboard?page=${page}&pageSize=50`;
  const genderQuery = `${gender ? `&gender=${gender}` : ''}`;
  const groupsQuery = `${groups.map((group) => `&groupIds[]=${group.id}`).join('')}`;
  const allGroups = `${groups.length !== 0 ? `&mustBeInAllGroups=${mustBeInAllGroups}` : ''}`;
  const rangeQuery = `${rangeType ? (`&rangeType=${rangeType}`) : ''}`;
  const rangeDatesQuery = (rangeDates && rangeType === 4
    && rangeDates.startDate && rangeDates.endDate)
    ? `&rangeMinDate=${format(new Date(rangeDates.startDate), 'yyyy-MM-dd')}&rangeMaxDate=${format(new Date(rangeDates.endDate), 'yyyy-MM-dd')}`
    : '';
  const compareToQuery = `${compareTo ? (`&compareTo=${compareTo}`) : ''}`;
  const compareDatesQuery = (compareDates && compareTo === 4
    && compareDates.startDate && compareDates.endDate)
    ? `&compareMinDate=${format(new Date(compareDates.startDate), 'yyyy-MM-dd')}&compareMaxDate=${format(new Date(compareDates.endDate), 'yyyy-MM-dd')}`
    : '';
  const downloadQuery = isCsv ? '&format=csv' : '';

  return `${accountString}${exerciseQuery}${leaderboardQuery}${genderQuery}${groupsQuery}${allGroups}${rangeQuery}${rangeDatesQuery}${compareToQuery}${compareDatesQuery}${downloadQuery}`;
};

const requestLeaderboardPage = (values, options) => (
  axiosAuthed.get(buildLeaderboardQuery(values, options))
);

// Shared by the thunks below: 403s are fully handled here (toast + board cleanup);
// download errors get their own toast since the modal's already closed; anything
// else is rethrown for the caller's in-modal/in-form error handling.
const handleLeaderboardFetchError = (
  dispatch,
  getState,
  err,
  { boardId, isDownload = false } = {},
) => {
  dispatch({ type: FETCH_LEADERBOARD_REJECTED, payload: err });
  if (err.response?.status === 403) {
    showErrorToast('You no longer have access to this leaderboard.');
    // eslint-disable-next-line no-use-before-define
    dispatch(fetchTemplates());
    if (boardId !== undefined) {
      dispatch({ type: CLOSE_BOARD, payload: { id: boardId } });
    }
    const remaining = Object.keys(getState().leaderboard.data.leaderboardsReference);
    if (remaining.length === 0) {
      dispatch({ type: SET_LEADERBOARD_VIEW, payload: { view: 'home', editingTemplateId: null } });
    }
    return; // 403 fully handled
  }
  if (isDownload) {
    showErrorToast(
      err?.response?.headers?.message || 'Failed to export leaderboard. Please try again.',
    );
    return; // modal already closed, toast is sufficient
  }
  throw err; // propagate to caller for in-modal/in-form error handling
};

export const addLeaderboard = (values, isTemplate = false, templateId = null) => (
  (dispatch, getState) => {
    const { exerciseName } = values;
    dispatch({ type: FETCH_LEADERBOARD_START });
    return requestLeaderboardPage(values, { page: 1 })
      .then((response) => new Promise((resolve) => {
        setTimeout(() => {
          batch(() => {
            dispatch({
              type: FETCH_LEADERBOARD_FULFILLED,
              payload: {
                data: response.data, exerciseName, values, isTemplate, templateId,
              },
            });
            dispatch({ type: SET_MODAL_OPEN, payload: { isModalOpen: false } });
          });
          resolve();
        }, 800);
      }))
      .catch((err) => handleLeaderboardFetchError(dispatch, getState, err));
  }
);

export const editLeaderboard = (values, id) => (
  (dispatch, getState) => {
    const { exerciseName } = values;
    dispatch({ type: EDIT_LEADERBOARD_START });
    return requestLeaderboardPage(values, { page: 1 })
      .then((response) => new Promise((resolve) => {
        setTimeout(() => {
          batch(() => {
            dispatch({
              type: EDIT_LEADERBOARD_FULFILLED,
              payload: {
                id, data: response.data, exerciseName, values,
              },
            });
            dispatch({ type: SET_MODAL_OPEN, payload: { isModalOpen: false } });
          });
          resolve();
        }, 800);
      }))
      .catch((err) => handleLeaderboardFetchError(dispatch, getState, err, { boardId: id }));
  }
);

// Only fields actually rendered/exported matter for detecting a "real" change —
// deliberately not a full deep-equal, since the API returns fresh objects on every
// request regardless of whether the underlying values changed.
const ROW_COMPARISON_KEYS = ['userId', 'rank', 'displayValue'];

export const haveLeaderboardRowsChanged = (previousRows = [], nextRows = []) => {
  if (previousRows.length !== nextRows.length) {
    return true;
  }
  return previousRows.some((prevRow, index) => (
    ROW_COMPARISON_KEYS.some((key) => prevRow[key] !== nextRows[index][key])
  ));
};

// Silent background reload (Board.js's auto-reload timer). Unlike editLeaderboard,
// this never closes a modal, and it re-fetches every page currently loaded (not just
// page 1) so rows past 50 don't go stale while a long board is being auto-scrolled
// through. If the previous page's isEndOfList was true, it also speculatively checks
// one page further — being wrong the other way (isEndOfList false but actually at
// the end) self-corrects the next time loadMore fires, so only this direction is
// worth an extra request for. Results are staged via STASH_PENDING_RELOAD; it's up to
// BoardTableWrapper to promote them into the visible board (see APPLY_PENDING_RELOAD).
export const reloadLeaderboard = (values, id) => (
  (dispatch, getState) => {
    const { exerciseName } = values;
    const {
      page: loadedPages = 1,
      isEndOfList: wasEndOfList = false,
    } = getState().leaderboard.data.leaderboards[id] || {};

    const pageNumbers = Array.from({ length: loadedPages }, (_, index) => index + 1);
    if (wasEndOfList) {
      pageNumbers.push(loadedPages + 1);
    }

    return Promise.all(pageNumbers.map((page) => requestLeaderboardPage(values, { page })))
      .then((responses) => {
        const extraPageRows = wasEndOfList ? responses[loadedPages].data : [];
        const nextRows = responses.slice(0, loadedPages)
          .flatMap((response) => response.data)
          .concat(extraPageRows);
        const nextPage = extraPageRows.length > 0 ? loadedPages + 1 : loadedPages;
        const nextIsEndOfList = wasEndOfList ? extraPageRows.length === 0 : false;

        const currentRows = getState().leaderboard.data.leaderboards[id]?.leaderboardData || [];
        if (!haveLeaderboardRowsChanged(currentRows, nextRows)) {
          return;
        }
        dispatch({
          type: STASH_PENDING_RELOAD,
          payload: {
            id,
            data: nextRows,
            exerciseName,
            values,
            page: nextPage,
            isEndOfList: nextIsEndOfList,
          },
        });
      })
      .catch((err) => handleLeaderboardFetchError(dispatch, getState, err, { boardId: id }));
  }
);

export const downloadLeaderboardCsv = (values, id) => (
  (dispatch, getState) => {
    const { exerciseName } = values;
    dispatch({ type: FETCH_LEADERBOARD_CSV_START });
    return requestLeaderboardPage(values, { page: 1, isCsv: true })
      .then((response) => {
        dispatch({ type: FETCH_LEADERBOARD_CSV_FULFILLED, payload: response.data });
        download(response.data, `Teambuildr ${exerciseName} Leaderboard.csv`, 'text/csv');
      })
      .catch((err) => (
        handleLeaderboardFetchError(dispatch, getState, err, { boardId: id, isDownload: true })
      ));
  }
);

export const loadMoreLeaderboard = (values, id) => (
  (dispatch, getState) => {
    const { exerciseName } = values;
    const currentPage = getState().leaderboard.data.leaderboards[id].page;
    const page = currentPage + 1;
    dispatch({ type: FETCH_NEXT_PAGE_START, payload: { id } });
    return requestLeaderboardPage(values, { page })
      .then((response) => {
        setTimeout(() => {
          dispatch({
            type: FETCH_NEXT_PAGE_FULFILLED,
            payload: {
              id,
              data: response.data,
              exerciseName,
              page: response.data.length === 0 ? currentPage : page,
            },
          });
        }, 800);
      })
      .catch((err) => {
        // Distinct from the shared FETCH_LEADERBOARD_REJECTED path below — this board's
        // own isLeaderboardLoading flag has no other way to clear on failure, and would
        // otherwise leave its infinite-scroll spinner stuck on indefinitely.
        dispatch({ type: FETCH_NEXT_PAGE_REJECTED, payload: { id } });
        return handleLeaderboardFetchError(dispatch, getState, err, { boardId: id });
      });
  }
);

export const closeBoard = (id) => (
  (dispatch) => {
    dispatch({ type: CLOSE_BOARD, payload: { id } });
  }
);

// Promotes whatever reloadLeaderboard staged via STASH_PENDING_RELOAD into the
// visible board. Callers (BoardTableWrapper) decide when it's safe to call this —
// this action creator itself makes no judgment about scroll position.
export const applyPendingReload = (id) => ({
  type: APPLY_PENDING_RELOAD,
  payload: { id },
});

export const updateSetting = (id, key, value) => ({
  type: UPDATE_SETTING,
  payload: {
    id,
    setting: key,
    value,
  },
});

export const setModalOpen = (isModalOpen, activeModal, activeBoardId) => ({
  type: SET_MODAL_OPEN,
  payload: {
    isModalOpen,
    activeModal,
    activeBoardId: !activeBoardId ? 0 : activeBoardId,
  },
});

// Converts an API template response to our internal Redux shape.
// Groups are resolved from the Redux store against the returned groupIds.
const exerciseTypeMap = { lift: 'L', saqc: 'R', circuit: 'C' };

const genderToNumber = {
  All: 0, Male: 1, Female: 2, Undefined: 3,
};

const rangeTypeToNumber = {
  AllTime: 1, ThirtyDays: 2, Today: 3, Custom: 4, Current: 5,
};

const compareToToNumber = {
  AllTime: 1,
  ThirtyDays: 2,
  Today: 3,
  Custom: 4,
  Current: 5,
};

const normalizeTemplate = (apiTemplate) => {
  const hasCustomRange = apiTemplate.rangeType === 'Custom'
    && apiTemplate.rangeMinDate && apiTemplate.rangeMaxDate;
  // Append T00:00:00 so the string is parsed as local midnight, not UTC midnight.
  // new Date('YYYY-MM-DD') follows ISO spec and gives UTC midnight, which shifts
  // the displayed date by one day in UTC- timezones.
  const localDate = (str) => new Date(`${str}T00:00:00`);
  const rangeDates = hasCustomRange
    ? {
      startDate: localDate(apiTemplate.rangeMinDate),
      endDate: localDate(apiTemplate.rangeMaxDate),
    }
    : {};

  const hasCustomCompare = apiTemplate.compareTo === 'Custom'
    && apiTemplate.compareToMinDate && apiTemplate.compareToMaxDate;
  const compareDates = hasCustomCompare
    ? {
      startDate: localDate(apiTemplate.compareToMinDate),
      endDate: localDate(apiTemplate.compareToMaxDate),
    }
    : {};

  return {
    id: apiTemplate.id,
    createdAt: apiTemplate.createdAt,
    category: exerciseTypeMap[apiTemplate.exercise?.type] || apiTemplate.exercise?.type,
    exerciseId: apiTemplate.exercise?.id,
    exerciseName: apiTemplate.exercise?.name,
    groups: apiTemplate.groups || [],
    mustBeInAllGroups: apiTemplate.mustBeInAllGroups,
    gender: genderToNumber[apiTemplate.gender] ?? 0,
    rangeType: rangeTypeToNumber[apiTemplate.rangeType] || 5,
    rangeDates,
    compareTo: compareToToNumber[apiTemplate.compareTo] ?? 0,
    compareDates,
  };
};

// Converts our internal Formik shape to the API request payload.
const denormalizeTemplate = (formValues) => {
  const {
    exerciseId,
    groups,
    mustBeInAllGroups,
    gender,
    rangeType,
    rangeDates,
    compareTo,
    compareDates,
  } = formValues;

  const rangeMinDate = (rangeType === 4 && rangeDates?.startDate)
    ? format(new Date(rangeDates.startDate), 'yyyy-MM-dd') : null;
  const rangeMaxDate = (rangeType === 4 && rangeDates?.endDate)
    ? format(new Date(rangeDates.endDate), 'yyyy-MM-dd') : null;
  const compareMinDate = (compareTo === 4 && compareDates?.startDate)
    ? format(new Date(compareDates.startDate), 'yyyy-MM-dd') : null;
  const compareMaxDate = (compareTo === 4 && compareDates?.endDate)
    ? format(new Date(compareDates.endDate), 'yyyy-MM-dd') : null;

  return {
    exerciseId,
    groupIds: (groups || []).map((g) => g.id),
    mustBeInAllGroups,
    gender,
    rangeType,
    rangeMinDate,
    rangeMaxDate,
    compareTo: compareTo || null,
    compareMinDate,
    compareMaxDate,
  };
};

export const fetchTemplates = () => (dispatch, getState) => {
  const { accountCode } = getState().auth.data.currentUser;
  dispatch({ type: FETCH_TEMPLATES_START });
  axiosAuthed.get(`/accounts/${accountCode}/leaderboards/templates`)
    .then((response) => {
      dispatch({
        type: FETCH_TEMPLATES_FULFILLED,
        payload: response.data.map((t) => normalizeTemplate(t)),
      });
    })
    .catch((err) => {
      dispatch({ type: FETCH_TEMPLATES_REJECTED, payload: err });
      if (err?.response?.status !== 403) {
        showErrorToast(err?.response?.headers?.message || 'Failed to load leaderboard templates. Please try again.');
      }
    });
};

export const markBoardAsTemplate = (boardId, templateId) => ({
  type: MARK_BOARD_AS_TEMPLATE,
  payload: { boardId, templateId },
});

export const saveTemplate = (formValues, boardId = null) => (dispatch, getState) => {
  const { accountCode } = getState().auth.data.currentUser;
  dispatch({ type: SAVE_TEMPLATE_START });
  return axiosAuthed.post(`/accounts/${accountCode}/leaderboards/templates`, denormalizeTemplate(formValues))
    .then((response) => {
      const normalized = normalizeTemplate(response.data);
      dispatch({ type: SAVE_TEMPLATE_FULFILLED, payload: normalized });
      if (boardId !== null) {
        dispatch(markBoardAsTemplate(boardId, normalized.id));
      }
      showSuccessToast('Leaderboard template saved successfully!');
      return normalized;
    })
    .catch((err) => {
      dispatch({ type: SAVE_TEMPLATE_REJECTED, payload: err });
      if (err.response?.status === 403) {
        showErrorToast('You no longer have access to create a template with the selected groups.');
        dispatch(fetchGroups(accountCode));
        return; // 403 fully handled
      }
      throw err;
    });
};

export const updateTemplate = (templateId, formValues, boardId = null) => (dispatch, getState) => {
  const { accountCode } = getState().auth.data.currentUser;
  dispatch({ type: UPDATE_TEMPLATE_START });
  return axiosAuthed.put(
    `/accounts/${accountCode}/leaderboards/templates/${templateId}`,
    denormalizeTemplate(formValues),
  )
    .then((response) => {
      const normalized = normalizeTemplate(response.data);
      dispatch({
        type: UPDATE_TEMPLATE_FULFILLED,
        payload: normalized,
      });
      showSuccessToast('Leaderboard template updated successfully!');
      return normalized;
    })
    .catch((err) => {
      dispatch({ type: UPDATE_TEMPLATE_REJECTED, payload: err });
      if (err.response?.status === 403) {
        showErrorToast('You no longer have access to one or more of this template\'s groups.');
        dispatch(fetchGroups(accountCode));
        dispatch(fetchTemplates());
        if (boardId !== null) {
          dispatch({ type: CLOSE_BOARD, payload: { id: boardId } });
          const remaining = Object.keys(getState().leaderboard.data.leaderboardsReference);
          if (remaining.length === 0) {
            dispatch({ type: SET_LEADERBOARD_VIEW, payload: { view: 'home', editingTemplateId: null } });
          }
        }
        return undefined; // 403 fully handled; falsy return signals "not a real success"
      }
      throw err;
    });
};

export const deleteTemplate = (templateId) => (dispatch, getState) => {
  const { accountCode } = getState().auth.data.currentUser;
  dispatch({ type: DELETE_TEMPLATE_START });
  axiosAuthed.delete(`/accounts/${accountCode}/leaderboards/templates/${templateId}`)
    .then(() => {
      dispatch({ type: DELETE_TEMPLATE_FULFILLED, payload: templateId });
      showSuccessToast('Leaderboard template deleted successfully!');
    })
    .catch((err) => {
      if (err.response?.status === 403) {
        showErrorToast('You no longer have access to delete this template.');
        dispatch(fetchTemplates());
      } else {
        showErrorToast(err?.response?.headers?.message || 'Failed to delete template. Please try again.');
      }
      dispatch({ type: DELETE_TEMPLATE_REJECTED, payload: err });
    });
};

export const setLeaderboardView = (view, editingTemplateId = null) => ({
  type: SET_LEADERBOARD_VIEW,
  payload: { view, editingTemplateId },
});

export const clearAllBoards = () => ({ type: CLEAR_ALL_BOARDS });

export const fetchAllExercises = (accountCode) => (
  (dispatch) => {
    dispatch({ type: FETCH_EXERCISES_START });
    axiosAuthed.get(`/accounts/${accountCode}/exercises?tracked=1`)
      .then((response) => {
        dispatch({
          type: FETCH_EXERCISES_FULFILLED,
          payload: response.data,
        });
      })
      .catch((err) => {
        dispatch({
          type: FETCH_EXERCISES_REJECTED,
          payload: err,
        });
      });
  }
);

export const setActiveFilters = (filters) => ({
  type: SET_ACTIVE_FILTERS,
  payload: filters,
});
